import { CircularProgress, Stack, useTheme } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ColoredAvatar, DynamicIcon, Tooltip } from 'src/components';
import { NavigationDataItem } from 'src/components/Navbar/use-navigation-items';
import { getNavigationTabCommand } from 'src/constants/appShortcuts';
import {
  type Address,
  type Client,
  type Conversation,
  type Project,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { useConversationStatusOptions } from 'src/graphql/entities/conversation-status';
import { MINI_SEARCH_SORTING } from 'src/graphql/entities/search/mini-search-sorting';
import { AnySearchableEntity, useSearch } from 'src/graphql/entities/search/use-search';
import {
  formatServiceLevelWithCpp,
  getServiceLevelColors,
} from 'src/graphql/entities/service-level';
import { EntityNames } from 'src/graphql/types';
import { useSearchTabs } from 'src/pages/Search/use-search-tabs';
import { useShortcutsStore } from 'src/store/shortcuts';
import { getHumanReadableDate } from 'src/utils/date';
import {
  useScrollFallbackWhenNotOverflowing,
  useScrollPaginateHandler,
} from 'src/utils/hooks';
import { TabEntity, TabType, useGlobalTabs } from 'src/utils/hooks/use-global-tabs';
import { useNavigateRoute } from 'src/utils/routing';
import { humanize } from 'src/utils/strings';
import { Platform } from 'src/utils/system';
import { HighlightedText } from './highlighted-text';
import {
  ClearButton,
  EmptyState,
  ExpandButton,
  FilterChip,
  FilterRow,
  ResultBadge,
  ResultMeta,
  ResultRow,
  ResultsList,
  ResultSubtitle,
  ResultTitle,
  SearchBarRow,
  SearchField,
  SearchInput,
} from './search-pane.styles';

// a fuller page keeps the compact pane useful; the same size is used for the
// first load and each scroll batch
const RESULTS_LIMIT = 30;

// platformSla + primaryClient come back on address search results but aren't on
// the exported Address type, so widen locally for the fields we read
type SiteSearchRecord = Address & {
  platformSla?: { displayName?: string | null } | null;
  primaryClient?: { fullName?: string | null } | null;
};

interface CompactResult {
  id: string;
  icon?: string;
  avatarName?: string;
  title: string;
  badge?: string;
  // status accent for the badge (open/solved/snoozed…); neutral chip when absent
  badgeColor?: string;
  subtitle?: string;
  // canonical navigation path; also what a shift-click opens in a browser tab
  path: string;
  // internal-tab descriptor for entities that support ProVision tabs; null for
  // Projects (no tab type, no full page) which open the grid with a side peek
  tab: TabEntity | null;
}

export interface SearchPaneProps {
  onNavigateToItem: (item: NavigationDataItem) => void;
  onExpand: () => void;
}

export const SearchPane = ({ onNavigateToItem, onExpand }: SearchPaneProps) => {
  const theme = useTheme();
  const { tabs } = useSearchTabs();
  const statusOptions = useConversationStatusOptions();
  const { navigate, partnerPrefix } = useNavigateRoute();
  const { openInTab } = useGlobalTabs();
  const { groupRules } = useGroupRules();
  const canUseTabs = groupRules[UserGroupRules.AccessToTabsInProvision];
  const getShortcutForCommand = useShortcutsStore((state) => state.getShortcutForCommand);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeEntity, setActiveEntity] = useState<EntityNames | null>(null);

  useEffect(() => {
    if (!activeEntity && tabs.length) {
      setActiveEntity(tabs[0].entity);
    }
  }, [activeEntity, tabs]);

  const entityName = activeEntity ?? EntityNames.Conversation;

  const { records, loadMore, hasMore, isLoading, isLoadingMore } =
    useSearch({
      entityName,
      searchTerm,
      sort: MINI_SEARCH_SORTING[entityName],
      limit: RESULTS_LIMIT,
      showResultsWhenEmptySearchTerm: false,
      skip: !activeEntity,
    }) ?? {};

  const resultsListRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 200,
    limit: RESULTS_LIMIT,
  });

  useScrollFallbackWhenNotOverflowing({
    containerRef: resultsListRef,
    onScroll: hasMore ? handleScroll : undefined,
    dataKey: records?.length ?? 0,
  });

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    inputRef.current?.focus();
  }, []);

  const resultRows = useCallback(
    () =>
      Array.from(
        resultsListRef.current?.querySelectorAll<HTMLElement>(
          '[data-testid="search-pane-result"]',
        ) ?? [],
      ),
    [],
  );

  // Tab from the field jumps straight into the results so they can be keyed
  // through, instead of tabbing across the clear/expand controls first
  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (
        event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey &&
        /^[1-9]$/.test(event.key)
      ) {
        const shortcut = getShortcutForCommand(getNavigationTabCommand(Number(event.key)));

        if (shortcut) {
          event.preventDefault();
          shortcut.callback();
          return;
        }
      }

      if (event.key !== 'Tab' || event.shiftKey) {
        return;
      }

      const [first] = resultRows();

      if (first) {
        event.preventDefault();
        first.focus();
      }
    },
    [getShortcutForCommand, resultRows],
  );

  // roving focus through the results; ArrowUp from the top returns to the field
  const handleResultsKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const { key } = event;

      if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Enter') {
        return;
      }

      // a focused result is a <button>, which Mousetrap (unlike inputs) does not
      // ignore — so up/down/enter would ALSO drive the grid's row shortcuts on
      // the right. Stop the event before it reaches the document-level binding.
      event.stopPropagation();

      // Enter activates the focused row via ButtonBase's native click
      if (key === 'Enter') {
        return;
      }

      const rows = resultRows();
      const index = rows.indexOf(document.activeElement as HTMLElement);
      event.preventDefault();

      if (key === 'ArrowDown') {
        rows[Math.min(index + 1, rows.length - 1)]?.focus();
      } else if (index <= 0) {
        inputRef.current?.focus();
      } else {
        rows[index - 1]?.focus();
      }
    },
    [resultRows],
  );

  const handleExpand = useCallback(() => {
    const query = searchTerm.trim();
    const path = `${portalPaths.search(entityName)}${
      query ? `?q=${encodeURIComponent(query)}` : ''
    }`;

    // full-page search takes over the main area, carrying the query; the nav
    // then returns Home (onExpand), which unmounts this pane — so the next time
    // the compact search is opened it starts fresh
    onNavigateToItem({
      id: 'search',
      path,
      title: 'Search',
      iconData: { name: 'search' },
    });
    onExpand();
  }, [entityName, onExpand, onNavigateToItem, searchTerm]);

  const results = useMemo<CompactResult[]>(() => {
    if (!records?.length) {
      return [];
    }

    const toResult = (record: AnySearchableEntity): CompactResult | null => {
      switch (entityName) {
        case EntityNames.Conversation: {
          const conversation = record as Conversation;
          const status = statusOptions.find(
            (option) => option.id === conversation.conversationStatusId,
          );

          return {
            id: conversation.id,
            icon: 'messages-square',
            title: conversation.subject || `Conversation #${conversation.friendlyId}`,
            badge:
              status?.name ??
              (conversation.conversationStatusId
                ? humanize(conversation.conversationStatusId)
                : undefined),
            badgeColor: status?.color,
            subtitle: joinMeta([
              conversation.requesterFullName,
              getHumanReadableDate(
                conversation.lastMessageReceivedAt ?? conversation.createdAt,
              ),
            ]),
            // the tab system keys conversations by friendlyId, so navigate with it
            path: portalPaths.conversation.full(conversation.friendlyId),
            tab: {
              type: TabType.Conversation,
              entity: {
                id: conversation.id,
                conversationId: conversation.conversationId,
                friendlyId: conversation.friendlyId,
                partnerId: conversation.partnerId,
                subject: conversation.subject,
                unseenCount: conversation.unseenCount,
              },
            },
          };
        }
        case EntityNames.Site: {
          const address = record as SiteSearchRecord;
          const title = address.fullAddress || address.businessName || address.name;

          if (!title) {
            return null;
          }

          // platformSla/primaryClient are populated on search records but absent
          // from the exported Address type (SiteSearchRecord widens for them)
          const serviceLevel = formatServiceLevelWithCpp(
            address.platformSla?.displayName,
            address.cppExpDate,
          );

          return {
            id: address.id,
            icon: 'building-2',
            title,
            badge: serviceLevel,
            badgeColor: getServiceLevelColors(serviceLevel, theme).color || undefined,
            subtitle: joinMeta([
              address.primaryClient?.fullName,
              title !== address.businessName ? address.businessName : undefined,
            ]),
            path: portalPaths.site.full(address.id),
            tab: {
              type: TabType.Site,
              entity: {
                id: address.id,
                addressId: address.addressId,
                partnerId: address.partnerId,
              },
            },
          };
        }
        case EntityNames.Client: {
          const client = record as Client;

          return {
            id: client.id,
            avatarName: client.fullName,
            title: client.fullName,
            subtitle: client.primaryEmail ?? undefined,
            path: portalPaths.client.full(client.id),
            tab: {
              type: TabType.Client,
              entity: {
                id: client.id,
                clientId: client.clientId,
                partnerId: client.partnerId,
              },
            },
          };
        }
        case EntityNames.Project: {
          const project = record as Project;

          return {
            id: project.id,
            icon: 'folder-open',
            title: project.name || 'Untitled project',
            subtitle: joinMeta([
              project.projectStage?.name,
              getHumanReadableDate(project.updatedAt),
            ]),
            // opened via the search grid (see handleResultClick), not the
            // projects grid — path is built there with the record id
            path: portalPaths.search(EntityNames.Project),
            tab: null,
          };
        }
        default:
          return null;
      }
    };

    return records.map(toResult).filter((result): result is CompactResult => !!result);
  }, [records, entityName, statusOptions, theme]);

  const handleResultClick = useCallback(
    (event: MouseEvent<HTMLElement>, result: CompactResult) => {
      // Projects have no tab type and no full page — open them in the search
      // grid (scoped to projects) with the record peeked, so the nav stays on
      // the search pane instead of being pulled back to Home by the projects grid.
      if (!result.tab) {
        const params = new URLSearchParams();
        const query = searchTerm.trim();
        if (query) {
          params.set('q', query);
        }
        params.set('recordId', result.id);
        navigate(`${portalPaths.search(EntityNames.Project)}?${params.toString()}`);
        return;
      }

      // Without tab access, fall back to plain full-page navigation.
      if (!canUseTabs) {
        navigate(result.path);
        return;
      }

      if (event.shiftKey) {
        // defer so the browser opens a tab rather than a blocked window
        setTimeout(() => {
          window.open(`${partnerPrefix ? `/${partnerPrefix}` : ''}${result.path}`, '_blank');
        }, 0);
        return;
      }

      const openInBackground = Platform.isMacOS ? event.metaKey : event.ctrlKey;
      openInTab(result.tab, undefined, !openInBackground);

      if (!openInBackground) {
        navigate(result.path);
      }
    },
    [canUseTabs, navigate, openInTab, partnerPrefix, searchTerm],
  );

  const hasQuery = Boolean(searchTerm.trim());

  return (
    <>
      <SearchBarRow>
        <SearchField>
          <DynamicIcon name="search" library="lucide" size={16} />
          <SearchInput
            autoFocus
            inputRef={inputRef}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder="Search conversations, sites, clients…"
            inputProps={{ 'data-testid': 'search-pane-input' }}
          />
          {hasQuery ? (
            <ClearButton
              disableRipple
              onClick={handleClear}
              aria-label="Clear search"
              data-testid="search-pane-clear"
            >
              <DynamicIcon name="x" library="lucide" size={14} />
            </ClearButton>
          ) : null}
        </SearchField>
        <Tooltip title="Full search opens on the right">
          <ExpandButton
            disableRipple
            onClick={handleExpand}
            aria-label="Open full-page search on the right"
            data-testid="search-pane-expand"
          >
            <DynamicIcon name="square-arrow-right-exit" library="lucide" size={16} />
          </ExpandButton>
        </Tooltip>
      </SearchBarRow>

      <FilterRow>
        {tabs.map((tab) => (
          <FilterChip
            key={tab.entity}
            disableRipple
            active={tab.entity === entityName}
            onClick={() => setActiveEntity(tab.entity)}
            data-testid={`search-pane-filter-${tab.entity}`}
          >
            {tab.label}
          </FilterChip>
        ))}
      </FilterRow>

      <ResultsList
        // remounting on entity switch re-runs the pane's block reveal, so the
        // whole results list animates in on a pill switch (matches the mockup)
        key={entityName}
        ref={resultsListRef}
        onScroll={handleScroll}
        onKeyDown={handleResultsKeyDown}
        data-testid="search-pane-results"
      >
        {results.map((result) => (
          <ResultRow
            key={result.id}
            disableRipple
            alignCenter={!result.subtitle}
            onClick={(event) => handleResultClick(event, result)}
            data-testid="search-pane-result"
          >
            {result.avatarName ? (
              <ColoredAvatar
                name={result.avatarName}
                sx={{ width: '1.5rem', height: '1.5rem' }}
              />
            ) : (
              <DynamicIcon name={result.icon!} library="lucide" size={18} />
            )}
            <ResultMeta>
              <ResultTitle>
                <span>
                  <HighlightedText text={result.title} query={searchTerm} />
                </span>
                {result.badge ? (
                  <ResultBadge
                    accent={result.badgeColor}
                    data-testid="search-pane-result-badge"
                  >
                    {result.badge}
                  </ResultBadge>
                ) : null}
              </ResultTitle>
              {result.subtitle ? (
                <ResultSubtitle data-testid="search-pane-result-subtitle">
                  <HighlightedText text={result.subtitle} query={searchTerm} />
                </ResultSubtitle>
              ) : null}
            </ResultMeta>
          </ResultRow>
        ))}

        {isLoading || isLoadingMore ? (
          <Stack alignItems="center" py={1.5}>
            <CircularProgress size="1.25rem" />
          </Stack>
        ) : null}

        {!hasQuery && !results.length && !isLoading ? (
          <EmptyState>Start typing to search across ProVision</EmptyState>
        ) : null}

        {hasQuery && !results.length && !isLoading ? (
          <EmptyState>No results found</EmptyState>
        ) : null}
      </ResultsList>
    </>
  );
};

function joinMeta(parts: (string | null | undefined)[]): string | undefined {
  const joined = parts.filter(Boolean).join(' · ');

  return joined || undefined;
}
